import { X, AlertTriangle, Ban } from 'lucide-react';

interface TokenLimitError {
  error?: string;
  message?: string;
  tokens_used?: number;
  token_limit?: number;
  estimated_usage?: number;
  tokens_remaining?: number;
  upgrade_url?: string;
}

interface TokenLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  tokenLimitError: TokenLimitError;
}

/**
 * TokenLimitModal - Modal para exibir erro de limite de tokens
 */
export const TokenLimitModal = ({
  isOpen,
  onClose,
  tokenLimitError,
}: TokenLimitModalProps) => {
  if (!isOpen) return null;

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  const isExceeded = tokenLimitError.error === 'token_limit_exceeded';
  const wouldExceed = tokenLimitError.error === 'token_limit_would_exceed';

  const usagePercentage = tokenLimitError.tokens_used && tokenLimitError.token_limit
    ? Math.min((tokenLimitError.tokens_used / tokenLimitError.token_limit) * 100, 100)
    : 0;

  return (
    <div
      className="fixed inset-0 w-screen h-screen bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="
          bg-[#1a1a1a] rounded-2xl
          w-full max-w-md
          border border-[#333333]
          shadow-2xl
          animate-in zoom-in-95 duration-200
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-4 p-6 border-b border-[#333333]">
          <div className={`
            w-12 h-12 rounded-full flex items-center justify-center
            ${isExceeded ? 'bg-red-500/20' : 'bg-orange-500/20'}
          `}>
            {isExceeded ? (
              <Ban size={24} className="text-red-500" />
            ) : (
              <AlertTriangle size={24} className="text-orange-500" />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-white font-semibold text-lg">
              {isExceeded ? 'Limite de Tokens Atingido' : 'Limite de Tokens Próximo'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="
              p-2 text-[#888888] hover:text-white
              rounded-lg hover:bg-white/5
              transition-colors
            "
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          <p className="text-[#CCCCCC] text-sm leading-relaxed">
            {tokenLimitError.message}
          </p>

          {/* Stats */}
          {tokenLimitError.tokens_used !== undefined && tokenLimitError.token_limit !== undefined && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#2a2a2a] rounded-lg p-3">
                  <div className="text-[#888888] text-xs mb-1">Tokens Utilizados</div>
                  <div className="text-white font-semibold">
                    {formatNumber(tokenLimitError.tokens_used)}
                  </div>
                </div>
                <div className="bg-[#2a2a2a] rounded-lg p-3">
                  <div className="text-[#888888] text-xs mb-1">Limite do Plano</div>
                  <div className="text-white font-semibold">
                    {formatNumber(tokenLimitError.token_limit)}
                  </div>
                </div>
              </div>

              {tokenLimitError.tokens_remaining !== undefined && (
                <div className="bg-[#2a2a2a] rounded-lg p-3">
                  <div className="text-[#888888] text-xs mb-1">Tokens Restantes</div>
                  <div className="text-[#F4CC04] font-semibold">
                    {formatNumber(tokenLimitError.tokens_remaining)}
                  </div>
                </div>
              )}

              {wouldExceed && tokenLimitError.estimated_usage !== undefined && (
                <div className="bg-[#2a2a2a] rounded-lg p-3">
                  <div className="text-[#888888] text-xs mb-1">Uso Estimado desta Mensagem</div>
                  <div className="text-orange-500 font-semibold">
                    {formatNumber(tokenLimitError.estimated_usage)}
                  </div>
                </div>
              )}

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-[#888888]">Uso atual</span>
                  <span className="text-[#F4CC04] font-medium">{usagePercentage.toFixed(1)}%</span>
                </div>
                <div className="h-2 bg-[#333333] rounded-full overflow-hidden">
                  <div
                    className={`
                      h-full rounded-full transition-all duration-500
                      ${usagePercentage >= 100 ? 'bg-red-500' : usagePercentage >= 90 ? 'bg-orange-500' : 'bg-[#F4CC04]'}
                    `}
                    style={{ width: `${usagePercentage}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="bg-[#2a2a2a] rounded-lg p-4">
            <h3 className="text-white font-medium text-sm mb-3">O que você pode fazer?</h3>
            <ul className="space-y-2 text-[#CCCCCC] text-sm">
              <li className="flex items-start gap-2">
                <span className="text-[#F4CC04] mt-0.5">•</span>
                <span>Entre em contato com um administrador para solicitar aumento de limite</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F4CC04] mt-0.5">•</span>
                <span>Aguarde a renovação mensal do seu limite</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F4CC04] mt-0.5">•</span>
                <span>Otimize suas mensagens para usar menos tokens</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-center p-6 border-t border-[#333333]">
          <button
            onClick={onClose}
            className="
              px-6 py-3
              bg-[#F4CC04] text-black
              rounded-lg font-semibold
              hover:bg-[#d4b104]
              transition-colors
            "
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
};
